import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Input, InputNumber, message, Modal, Select, Spin } from 'antd';
import { getPetBreedListApi, PetBreedItem } from '@/api/petManage/petBreed';
import { getPetTagListApi, PetTagItem } from '@/api/petManage/petTag';
import {
  getLifeStageByPetTypeApi,
  getPetTypeListApi,
  PetLifeStageItem,
  PetTypeDictItem,
} from '@/api/petManage/petType';
import { getGoodsRecommendDetailApi } from '@/api/pointCenter/goodsRecommend';
import ImageUploader from '@/component/ImageUploader';
import { MAX_RECOMMEND_GOODS_COUNT } from '../constants';

type RuleEditorMode = 'create' | 'edit' | 'view';

type RuleEditorModalProps = {
  open: boolean;
  mode: RuleEditorMode;
  shopId: number | string;
  taskId?: number | string;
  ruleId?: number | string | null;
  platformType?: string | null;
  initialData?: any;
  onSubmit?: (payload: any) => Promise<void> | void;
  onCancel: () => void;
  onSuccess: () => void;
};

const DEFAULT_FORM_VALUES = {
  ruleId: '',
  petType: undefined,
  petBreed: undefined,
  ageStage: undefined,
  tag: undefined,
  purchasedGoodsList: [{}],
  unPurchasedGoodsList: [{}],
};

const TAG_PAGE_SIZE = 1000;

const RuleEditorModal: React.FC<RuleEditorModalProps> = ({
  open,
  mode,
  shopId,
  taskId,
  ruleId,
  platformType,
  initialData,
  onSubmit,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [petTypeOptions, setPetTypeOptions] = useState<{ label: string; value: string }[]>([]);
  const [ageStageOptions, setAgeStageOptions] = useState<{ label: string; value: string }[]>([]);
  const [breedOptions, setBreedOptions] = useState<{ label: string; value: string }[]>([]);
  const [tagOptions, setTagOptions] = useState<{ label: string; value: string }[]>([]);
  const [ageStageLoading, setAgeStageLoading] = useState(false);
  const [breedLoading, setBreedLoading] = useState(false);
  const [tagLoading, setTagLoading] = useState(false);
  const [tagPageNum, setTagPageNum] = useState(1);
  const [tagTotal, setTagTotal] = useState(0);

  const isView = mode === 'view';
  const showItemId = platformType === 'TM';

  const modalTitle = useMemo(() => {
    if (mode === 'edit') return '编辑推荐规则';
    if (mode === 'view') return '查看推荐规则';
    return '新建推荐规则';
  }, [mode]);

  const fetchPetTypes = async () => {
    try {
      const res = await getPetTypeListApi();
      const list: PetTypeDictItem[] = Array.isArray(res?.data) ? res.data : [];
      if (res?.code === 200) {
        setPetTypeOptions(
          list.map(item => ({
            label: item.dictName,
            value: String(item.dictCode ?? ''),
          })),
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAgeStages = async (petType?: string | number) => {
    if (petType === undefined || petType === null || petType === '') {
      setAgeStageOptions([]);
      return;
    }
    setAgeStageLoading(true);
    try {
      const res = await getLifeStageByPetTypeApi(petType);
      const list: PetLifeStageItem[] = Array.isArray(res?.data) ? res.data : [];
      if (res?.code === 200) {
        setAgeStageOptions(
          list.map(item => ({
            label: item.stageName,
            value: String(item.id ?? ''),
          })),
        );
      } else {
        setAgeStageOptions([]);
      }
    } catch (error) {
      console.error(error);
      setAgeStageOptions([]);
    } finally {
      setAgeStageLoading(false);
    }
  };

  const fetchBreeds = async (petType?: string | number) => {
    if (petType === undefined || petType === null || petType === '') {
      setBreedOptions([]);
      return;
    }
    setBreedLoading(true);
    try {
      const res = await getPetBreedListApi({ petType });
      const list: PetBreedItem[] = Array.isArray(res?.data) ? res.data : [];
      if (res?.code === 200) {
        setBreedOptions(
          list.map(item => ({
            label: item.breedName || '',
            value: String(item.id ?? ''),
          })),
        );
      } else {
        setBreedOptions([]);
      }
    } catch (error) {
      console.error(error);
      setBreedOptions([]);
    } finally {
      setBreedLoading(false);
    }
  };

  const fetchTags = async (page = 1) => {
    setTagLoading(true);
    try {
      const res = await getPetTagListApi({ pageNum: page, pageSize: TAG_PAGE_SIZE });
      const rows: PetTagItem[] = Array.isArray(res?.rows) ? res.rows : [];
      const total = typeof res?.total === 'number' ? res.total : 0;
      const options = rows.map(item => ({
        label: item.tagName || '',
        value: String(item.id ?? ''),
      }));
      setTagOptions(prev => (page === 1 ? options : [...prev, ...options]));
      setTagPageNum(page);
      setTagTotal(total);
    } catch (error) {
      console.error(error);
    } finally {
      setTagLoading(false);
    }
  };

  const formatGoodsListForForm = (list?: any[]) => {
    if (!Array.isArray(list) || !list.length) {
      return [{}];
    }
    return [...list]
      .sort((a, b) => Number(a?.priorityWeight || 0) - Number(b?.priorityWeight || 0))
      .map(item => ({ ...item }));
  };

  useEffect(() => {
    if (!open) return;
    fetchPetTypes();
    fetchTags(1);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setAgeStageOptions([]);
    setBreedOptions([]);
    form.setFieldsValue(DEFAULT_FORM_VALUES);
    if (initialData) {
      const detail = initialData;
      if (detail.petType) {
        fetchAgeStages(detail.petType);
        fetchBreeds(detail.petType);
      }
      form.setFieldsValue({
        ...DEFAULT_FORM_VALUES,
        ...detail,
        ruleId: detail.ruleId ?? detail.id ?? ruleId,
        petType:
          detail.petType === undefined || detail.petType === null || detail.petType === ''
            ? undefined
            : String(detail.petType),
        petBreed:
          detail.petBreed === undefined || detail.petBreed === null || detail.petBreed === ''
            ? undefined
            : String(detail.petBreed),
        ageStage:
          detail.ageStage === undefined || detail.ageStage === null || detail.ageStage === ''
            ? undefined
            : String(detail.ageStage),
        tag: detail.tag === undefined || detail.tag === null || detail.tag === '' ? undefined : String(detail.tag),
        purchasedGoodsList: formatGoodsListForForm(detail.purchasedGoodsList),
        unPurchasedGoodsList: formatGoodsListForForm(detail.unPurchasedGoodsList),
      });
      setLoading(false);
      return;
    }
    if (mode === 'create' || !ruleId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const res = await getGoodsRecommendDetailApi({ id: ruleId, shopId });
        if (res?.code === 200 && res?.data) {
          const detail = res.data;
          if (detail.petType) {
            await Promise.all([fetchAgeStages(detail.petType), fetchBreeds(detail.petType)]);
          }
          form.setFieldsValue({
            ...DEFAULT_FORM_VALUES,
            ...detail,
            ruleId: detail.ruleId ?? detail.id ?? ruleId,
            petType:
              detail.petType === undefined || detail.petType === null || detail.petType === ''
                ? undefined
                : String(detail.petType),
            petBreed:
              detail.petBreed === undefined || detail.petBreed === null || detail.petBreed === ''
                ? undefined
                : String(detail.petBreed),
            ageStage:
              detail.ageStage === undefined || detail.ageStage === null || detail.ageStage === ''
                ? undefined
                : String(detail.ageStage),
            tag: detail.tag === undefined || detail.tag === null || detail.tag === '' ? undefined : String(detail.tag),
            purchasedGoodsList: formatGoodsListForForm(detail.purchasedGoodsList),
            unPurchasedGoodsList: formatGoodsListForForm(detail.unPurchasedGoodsList),
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [form, initialData, mode, open, ruleId, shopId]);

  const hasAnyGoodsField = (item: any) => {
    const keys = showItemId
      ? ['imgUrl', 'itemId', 'skuId', 'productName', 'skuPrice', 'sellingPoint']
      : ['imgUrl', 'skuId', 'productName', 'skuPrice', 'sellingPoint'];
    return keys.some(key => {
      const value = item?.[key];
      return value !== undefined && value !== null && value !== '';
    });
  };

  const validateGoodsList = (list: any[] = [], label: string) => {
    const requiredFields = showItemId
      ? ['imgUrl', 'itemId', 'skuId', 'productName', 'skuPrice', 'sellingPoint']
      : ['imgUrl', 'skuId', 'productName', 'skuPrice', 'sellingPoint'];
    const fieldLabels: Record<string, string> = {
      imgUrl: '缩略图',
      itemId: 'itemId',
      skuId: 'SKUID',
      productName: '商品名称',
      skuPrice: '售价',
      sellingPoint: '卖点',
    };
    const normalizedList: any[] = [];
    list.forEach((item, index) => {
      if (!item || !hasAnyGoodsField(item)) return;
      requiredFields.forEach(field => {
        const value = item[field];
        if (value === undefined || value === null || value === '') {
          throw new Error(`${label}第${index + 1}条${fieldLabels[field]}未填写`);
        }
      });
      normalizedList.push({
        ...item,
        priorityWeight: normalizedList.length + 1,
      });
    });
    if (normalizedList.length > MAX_RECOMMEND_GOODS_COUNT) {
      throw new Error(`${label}最多创建${MAX_RECOMMEND_GOODS_COUNT}条商品信息`);
    }
    return normalizedList.map(item => {
      const payload = { ...item };
      if (!showItemId) {
        delete payload.itemId;
      }
      return payload;
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const purchasedGoodsList = validateGoodsList(values.purchasedGoodsList || [], '已购推荐商品');
      const unPurchasedGoodsList = validateGoodsList(values.unPurchasedGoodsList || [], '未购推荐商品');
      if (!purchasedGoodsList.length && !unPurchasedGoodsList.length) {
        message.error('已购或未购至少填写一条商品信息');
        return;
      }
      const payload = {
        shopId,
        taskId,
        ruleId: values.ruleId || ruleId || '',
        petType: values.petType,
        petBreed: values.petBreed,
        ageStage: values.ageStage,
        tag: values.tag,
        purchasedGoodsList,
        unPurchasedGoodsList,
      };
      setSubmitting(true);
      if (onSubmit) {
        await onSubmit({
          ...payload,
          _display: {
            petTypeLabel: petTypeOptions.find(item => String(item.value) === String(values.petType))?.label,
            ageStageLabel: ageStageOptions.find(item => String(item.value) === String(values.ageStage))?.label,
            petBreedLabel: breedOptions.find(item => String(item.value) === String(values.petBreed))?.label,
            tagLabel: tagOptions.find(item => String(item.value) === String(values.tag))?.label,
          },
        });
        onSuccess();
        return;
      }
      throw new Error('缺少规则保存处理函数');
    } catch (error: any) {
      if (error?.errorFields) return;
      if (error instanceof Error && error.message) {
        message.error(error.message);
        return;
      }
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderGoodsList = (fieldName: 'purchasedGoodsList' | 'unPurchasedGoodsList', title: string) => (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontWeight: 600, marginBottom: 12 }}>{title}</div>
      <Form.List name={fieldName}>
        {(fields, { add, remove, move }) => {
          const listLength = fields.length;
          const gridTemplateColumns = showItemId
            ? '80px 1fr 1fr 1fr 1.1fr 1fr 1.1fr 120px'
            : '80px 1fr 1fr 1.1fr 1fr 1.1fr 120px';
          return (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns,
                  columnGap: 12,
                  padding: '8px 0',
                  fontWeight: 600,
                  color: '#666',
                }}>
                <div>优先级</div>
                <div>缩略图</div>
                {showItemId ? <div>itemId</div> : null}
                <div>SKUID</div>
                <div>商品名称</div>
                <div>售价</div>
                <div>卖点</div>
                <div>操作</div>
              </div>
              {fields.map((field, index) => (
                <div
                  key={field.key}
                  style={{
                    display: 'grid',
                    gridTemplateColumns,
                    columnGap: 12,
                    padding: '12px 0',
                    borderTop: '1px solid #f0f0f0',
                    alignItems: 'start',
                  }}>
                  <div style={{ paddingTop: 8 }}>{index + 1}</div>
                  <Form.Item
                    name={[field.name, 'imgUrl']}
                    style={{ marginBottom: 0 }}>
                    <ImageUploader
                      width={80}
                      height={80}
                      limitWidth={200}
                      limitHeight={200}
                      maxSizeKB={5000}
                      disabled={isView}
                    />
                  </Form.Item>
                  {showItemId ? (
                    <Form.Item
                      name={[field.name, 'itemId']}
                      style={{ marginBottom: 0 }}>
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder="请输入"
                        min={0}
                        precision={0}
                        stringMode
                        disabled={isView}
                      />
                    </Form.Item>
                  ) : null}
                  <Form.Item
                    name={[field.name, 'skuId']}
                    style={{ marginBottom: 0 }}>
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="请输入"
                      min={0}
                      precision={0}
                      stringMode
                      disabled={isView}
                    />
                  </Form.Item>
                  <Form.Item
                    name={[field.name, 'productName']}
                    style={{ marginBottom: 0 }}
                    normalize={val => (typeof val === 'string' ? val.replace(/^-+/, '') : val)}>
                    <Input
                      placeholder="请输入"
                      maxLength={50}
                      showCount
                      disabled={isView}
                    />
                  </Form.Item>
                  <Form.Item
                    name={[field.name, 'skuPrice']}
                    style={{ marginBottom: 0 }}>
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="请输入售价"
                      min={0}
                      max={99999.99}
                      precision={2}
                      stringMode
                      disabled={isView}
                    />
                  </Form.Item>
                  <Form.Item
                    name={[field.name, 'sellingPoint']}
                    style={{ marginBottom: 0 }}
                    normalize={val => (typeof val === 'string' ? val.replace(/^-+/, '') : val)}>
                    <Input
                      placeholder="请输入"
                      maxLength={50}
                      showCount
                      disabled={isView}
                    />
                  </Form.Item>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingTop: 6 }}>
                    {!isView && (
                      <>
                        <Button
                          type="text"
                          icon={<ArrowUpOutlined />}
                          disabled={index === 0}
                          onClick={() => move(index, index - 1)}
                        />
                        <Button
                          type="text"
                          icon={<ArrowDownOutlined />}
                          disabled={index === fields.length - 1}
                          onClick={() => move(index, index + 1)}
                        />
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                        />
                      </>
                    )}
                  </div>
                </div>
              ))}
              {!isView && (
                <Button
                  style={{ marginTop: 12 }}
                  block
                  type="dashed"
                  icon={<PlusOutlined />}
                  disabled={listLength >= MAX_RECOMMEND_GOODS_COUNT}
                  onClick={() => add({})}>
                  添加推荐商品 {`${Math.min(listLength, MAX_RECOMMEND_GOODS_COUNT)}/${MAX_RECOMMEND_GOODS_COUNT}`}
                </Button>
              )}
            </>
          );
        }}
      </Form.List>
    </div>
  );

  return (
    <Modal
      title={modalTitle}
      open={open}
      width="80vw"
      destroyOnClose
      onCancel={onCancel}
      onOk={isView ? onCancel : handleSubmit}
      okText={isView ? '关闭' : '确认'}
      cancelText={isView ? '返回' : '取消'}
      confirmLoading={submitting}
      okButtonProps={{ disabled: loading }}>
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={DEFAULT_FORM_VALUES}>
          <Form.Item
            name="ruleId"
            hidden>
            <Input />
          </Form.Item>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              columnGap: 24,
            }}>
            <Form.Item
              label="品种类型"
              name="petType"
              rules={[{ required: true, message: '请选择品种类型' }]}>
              <Select
                placeholder="请选择"
                allowClear
                disabled={isView}
                options={petTypeOptions}
                showSearch
                optionFilterProp="label"
                onChange={value => {
                  form.setFieldsValue({ petType: value, ageStage: undefined, petBreed: undefined });
                  fetchAgeStages(value);
                  fetchBreeds(value);
                }}
              />
            </Form.Item>
            <Form.Item
              label="品种"
              name="petBreed"
              rules={[{ required: true, message: '请选择品种' }]}>
              <Select
                placeholder="请选择"
                allowClear
                disabled={isView}
                loading={breedLoading}
                options={breedOptions}
                showSearch
                optionFilterProp="label"
                onFocus={() => fetchBreeds(form.getFieldValue('petType'))}
              />
            </Form.Item>
            <Form.Item
              label="年龄段"
              name="ageStage"
              rules={[{ required: true, message: '请选择年龄段' }]}>
              <Select
                placeholder="请选择"
                allowClear
                disabled={isView}
                loading={ageStageLoading}
                options={ageStageOptions}
                showSearch
                optionFilterProp="label"
                onFocus={() => fetchAgeStages(form.getFieldValue('petType'))}
              />
            </Form.Item>
            <Form.Item
              label="标签"
              name="tag">
              <Select
                placeholder="请选择"
                allowClear
                disabled={isView}
                loading={tagLoading}
                options={tagOptions}
                showSearch
                optionFilterProp="label"
                onOpenChange={nextOpen => {
                  if (nextOpen && !tagOptions.length) {
                    fetchTags(1);
                  }
                }}
                onPopupScroll={e => {
                  const target = e.target as HTMLDivElement;
                  if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 20) {
                    const hasMore = tagOptions.length < tagTotal;
                    if (!tagLoading && hasMore) {
                      fetchTags(tagPageNum + 1);
                    }
                  }
                }}
              />
            </Form.Item>
          </div>
          {renderGoodsList('purchasedGoodsList', '已购推荐商品')}
          {renderGoodsList('unPurchasedGoodsList', '未购推荐商品')}
        </Form>
      </Spin>
    </Modal>
  );
};

export default RuleEditorModal;
